import type { Dispatch, SetStateAction } from 'react';
import { toError } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import type { ProviderSummary, ProviderTestResult } from '@/api/schemas/ai';

/**
 * The provider list's row actions.
 *
 * They are ordinary functions rather than a custom hook so that the state
 * they touch is stated explicitly: each one takes the context it needs and
 * returns a promise. Nothing here decides anything about the UI — the
 * components decide what a failure looks like.
 */
export type ProviderActionContext = {
  setBusyId: (id: string | null) => void;
  setActionError: (error: Error | null) => void;
  setTestResults: Dispatch<SetStateAction<Record<string, ProviderTestResult>>>;
  reload: () => void;
};

export type ProviderActionHandlers = {
  /** Switches a provider between `active` and `disabled`. */
  toggleState: (provider: ProviderSummary) => Promise<void>;
  /** Runs a connection test and keeps the result for the row to show. */
  test: (provider: ProviderSummary) => Promise<void>;
  /** Replaces the provider's credential. The key is write-only. */
  rotate: (provider: ProviderSummary, apiKey: string) => Promise<void>;
};

export function providerActionHandlers(context: ProviderActionContext): ProviderActionHandlers {
  return {
    toggleState: async (provider) => {
      context.setBusyId(provider.id);
      context.setActionError(null);
      try {
        const next = provider.state === 'active' ? 'disabled' : 'active';
        await aiApi.updatePlatformProvider(provider.id, { state: next }, provider.version);
        context.reload();
      } catch (cause) {
        context.setActionError(toError(cause));
      } finally {
        context.setBusyId(null);
      }
    },

    test: async (provider) => {
      context.setBusyId(provider.id);
      context.setActionError(null);
      try {
        const result = await aiApi.testPlatformProvider(provider.id);
        context.setTestResults((previous) => ({ ...previous, [provider.id]: result }));
      } catch (cause) {
        context.setActionError(toError(cause));
      } finally {
        context.setBusyId(null);
      }
    },

    rotate: async (provider, apiKey) => {
      context.setBusyId(provider.id);
      context.setActionError(null);
      try {
        await aiApi.rotatePlatformCredential(provider.id, { apiKey });
        context.reload();
      } catch (cause) {
        context.setActionError(toError(cause));
      } finally {
        context.setBusyId(null);
      }
    },
  };
}
